import moment from 'moment-timezone';
import set from 'lodash/set';

import api from '../../../lib/api';
import utils from '../../../lib/utils';

const employeeTaxRegime = '605';
const invalidPerception = ['022', '023', '025', '039', '044'];
const otherPaymentPeriodicity = '99';

export default {
  async create({ state, rootState, commit }) {
    const {
      type,
      paymentDate,
      paymentPeriodStart,
      paymentPeriodEnd,
      earnedDays,
      percepts,
      deductions,
      otherPayments,
      paidLeaves,
    } = state;

    const { employee, company, auth } = rootState;

    const totalPercepts = percepts.reduce((accum, percept) => {
      if (invalidPerception.includes(percept.type)) return parseFloat(accum);

      const amount = parseFloat(percept.taxedAmount) + parseFloat(percept.exemptAmount);

      return parseFloat(amount) + parseFloat(accum);
    }, 0.00);

    const totalPerceptTaxed = percepts.reduce(
      (accum, percept) => parseFloat(accum) + parseFloat(percept.taxedAmount), 0.00,
    );

    const totalPerceptExempt = percepts.reduce(
      (accum, percept) => parseFloat(accum) + parseFloat(percept.exemptAmount), 0.00,
    );

    const totalOtherPayments = otherPayments.reduce(
      (accum, otherPayment) => parseFloat(accum) + parseFloat(otherPayment.amount), 0.00,
    );

    const totalPaidLeaves = paidLeaves.reduce(
      (accum, paidLeave) => parseFloat(accum) + parseFloat(paidLeave.amount), 0.00,
    );

    const totalPayroll = parseFloat(totalPercepts) + parseFloat(totalOtherPayments);

    const totalDeductions = deductions.reduce(
      (accum, deduction) => parseFloat(accum) + parseFloat(deduction.amount),
      0.00,
    );

    const totalTaxesWithheld = deductions.reduce((accum, { deductionType, amount }) => {
      if (deductionType === '002') return parseFloat(accum) + parseFloat(amount);
      return parseFloat(accum);
    }, 0.00);

    const totalOtherDeductions = deductions.reduce((accum, { deductionType, amount }) => {
      if (deductionType !== '002') return parseFloat(accum) + parseFloat(amount);
      return parseFloat(accum);
    }, 0.00);

    const convertedPaymentDate = typeof paymentDate !== 'number'
      ? moment(new Date(paymentDate)).unix() : paymentDate;

    const convertedPaymentPeriodStart = typeof paymentPeriodStart !== 'number'
      ? moment(new Date(paymentPeriodStart)).unix() : paymentPeriodStart;

    const convertedPaymentPeriodEnd = typeof paymentPeriodEnd !== 'number'
      ? moment(new Date(paymentPeriodEnd)).unix() : paymentPeriodEnd;

    const convertedRelationShipStart = typeof employee.jobRelationshipStart !== 'number'
      ? moment(new Date(employee.jobRelationshipStart)).unix() : employee.jobRelationshipStart;

    const receiverSeniorityWeeks = (moment(new Date(convertedPaymentPeriodEnd * 1000))
      .diff(moment(new Date(convertedRelationShipStart * 1000)), 'week'));

    // eslint-disable-next-line
    const employeeRegistration =  isNaN(receiverSeniorityWeeks) ? null : company.employerRegistration;

    const payroll = {
      payrollVersion: '1.2',
      payrollType: type,
      payrollPaymentDate: convertedPaymentDate,
      payrollPaymentPeriodStart: convertedPaymentPeriodStart,
      payrollPaymentPeriodEnd: convertedPaymentPeriodEnd,
      payrollEarnedDays: earnedDays,
      payrollTotalPercepts: utils.fixFloat(totalPercepts, 2),
      payrollTotalDeductions: utils.fixFloat(totalDeductions, 2),
      payrollTotalOtherPayments: utils.fixFloat(totalOtherPayments, 2),
      emitterCurp: company.curp || null,
      emitterEmployerRegistration: ['09', '10', '99'].includes(employee.contractType) ? null : employeeRegistration,
      emitterOriginEmployerRfc: employee.originEmployerCurp || null,
      receiverCurp: employee.curp,
      receiverSocialSecurityNumber: employee.socialSecurityNumber,
      receiverJobRelationshipStart: moment(employee.jobRelationshipStart).unix(),
      // eslint-disable-next-line
      receiverSeniority: isNaN(receiverSeniorityWeeks) ? null : `P${receiverSeniorityWeeks}W`,
      receiverContractType: employee.contractType,
      receiverUnionized: employee.unionized,
      receiverWorkingDayType: employee.workingDayType,
      receiverRegimeType: employee.regimeType,
      receiverEmployedNumber: employee.employedNumber,
      receiverDepartment: employee.department,
      receiverPosition: employee.position,
      receiverHazardPosition: employee.hazardPosition,
      receiverPaymentPeriodicity: employee.paymentPeriodicity,
      receiverBank: employee.bank,
      receiverBankAccount: employee.bankAccount,
      receiverBaseSalary: employee.baseSalary,
      receiverDailyBaseSalary: employee.dailyBaseSalary,
      receiverLocalityKey: employee.localityKey,
      employeeId: employee.id,
      perceptsTotalEarnings: utils.fixFloat(totalPercepts, 2),
      perceptsTotalCompensation: utils.fixFloat(totalPaidLeaves, 2),
      perceptsTotalTaxed: utils.fixFloat(totalPerceptTaxed, 2),
      perceptsTotalExempt: utils.fixFloat(totalPerceptExempt, 2),
      deductionsTotalOtherDeductions: utils.fixFloat(totalOtherDeductions, 2),
      deductionsTotalTaxWithheld: utils.fixFloat(totalTaxesWithheld, 2),
      percepts,
      deductions,
      otherPayments,
      paidLeaves,
    };

    if (!payroll.emitterCurp) delete payroll.emitterCurp;

    if (parseFloat(payroll.perceptsTotalCompensation) === 0.00) {
      delete payroll.perceptsTotalCompensation;
    }

    if (parseFloat(payroll.deductionsTotalTaxWithheld) === 0.00) {
      delete payroll.deductionsTotalTaxWithheld;
    }

    if (parseFloat(payroll.deductionsTotalOtherDeductions) === 0.00) {
      delete payroll.deductionsTotalOtherDeductions;
    }

    if (payroll.percepts.length === 0) {
      delete payroll.percepts;
    }

    if (payroll.deductions.length === 0) {
      delete payroll.payrollTotalDeductions;
      delete payroll.deductions;
    }

    if (payroll.otherPayments.length === 0) {
      delete payroll.otherPayments;
      delete payroll.payrollTotalOtherPayments;
    }

    if (payroll.paidLeaves.length === 0) {
      delete payroll.paidLeaves;
    }

    if (payroll.payrollType === 'E') {
      payroll.receiverPaymentPeriodicity = otherPaymentPeriodicity;
    }

    const cfdi = {
      email: employee.email,
      name: employee.name,
      voucherExpeditionPlace: company.zipCode,
      voucherType: 'N',
      voucherCurrency: 'MXN',
      voucherSeries: 'N',
      voucherPaymentMethod: 'PUE',
      voucherDate: moment().unix(),
      voucherDiscount: utils.fixFloat(totalDeductions, 2),
      voucherTotal: utils.fixFloat((totalPayroll - totalDeductions), 2),
      voucherSubTotal: utils.fixFloat(totalPayroll, 2),
      exportation: '01',
      emitterRegime: company.regime,
      emitterName: company.name,
      emitterRfc: company.rfc,
      receiverCfdiUsage: 'CN01',
      receiverName: employee.name,
      receiverRfc: employee.rfc,
      receiverZipCode: employee.zipCode,
      receiverTaxRegime: employeeTaxRegime,
      concepts: [{
        productServiceKey: '84111505',
        description: 'Pago de nómina',
        quantity: 1,
        unitKey: 'ACT',
        amount: utils.fixFloat(totalPayroll, 2),
        unit: utils.fixFloat(totalPayroll, 2),
        discount: utils.fixFloat(totalDeductions, 2),
        subjectToTax: '01',
      }],
      complement: {
        payroll: Object.keys(payroll).reduce((accum, key) => {
          if (payroll[key]) set(accum, key, payroll[key]);
          return accum;
        }, {}),
      },
    };

    if (!payroll.deductions) {
      delete cfdi.voucherDiscount;
      delete cfdi.concepts[0].discount;
    }

    const payload = await api.invoice.save(auth.companyId, cfdi, true);

    commit('setValues', {
      type: 'O',
      paymentDate: '',
      paymentPeriodStart: '',
      paymentPeriodEnd: '',
      earnedDays: '',
      percepts: [{
        perceptType: null,
        key: null,
        concept: null,
        taxedAmount: null,
        exemptAmount: null,
        overtime: [],
      }],
      deductions: [],
      otherPayments: [],
      paidLeaves: [],
    });


    commit('employee/setValues', {
      id: null,
      name: '',
      email: '',
      rfc: '',
      curp: '',
      socialSecurityNumber: '',
      jobRelationshipStart: '',
      seniority: '',
      contractType: '',
      unionized: '',
      workingDayType: '',
      regimeType: '',
      employedNumber: '',
      department: '',
      position: '',
      hazardPosition: '',
      paymentPeriodicity: '',
      bank: '',
      bankAccount: '',
      baseSalary: '',
      dailyBaseSalary: '',
      zipCode: '',
      localityKey: '',
    }, { root: true });

    return payload;
  },

  async createSalaryReceipt({ state, rootState, commit }) {
    const {
      paymentDate,
      paymentPeriodStart,
      paymentPeriodEnd,
      earnedDays,
      percepts,
    } = state;

    const { employee, company, auth } = rootState;

    const totalPercepts = percepts.reduce((accum, percept) => {
      if (invalidPerception.includes(percept.type)) return parseFloat(accum);

      const amount = parseFloat(percept.taxedAmount);

      return parseFloat(amount) + parseFloat(accum);
    }, 0.00);

    const totalPayroll = parseFloat(totalPercepts);
    const convertedPaymentDate = typeof paymentDate !== 'number'
      ? moment(new Date(paymentDate)).unix() : paymentDate;

    const convertedPaymentPeriodStart = typeof paymentPeriodStart !== 'number'
      ? moment(new Date(paymentPeriodStart)).unix() : paymentPeriodStart;

    const convertedPaymentPeriodEnd = typeof paymentPeriodEnd !== 'number'
      ? moment(new Date(paymentPeriodEnd)).unix() : paymentPeriodEnd;

    const payroll = {
      payrollVersion: '1.2',
      payrollPaymentDate: convertedPaymentDate,
      payrollPaymentPeriodStart: convertedPaymentPeriodStart,
      payrollPaymentPeriodEnd: convertedPaymentPeriodEnd,
      payrollEarnedDays: earnedDays,
      payrollTotalPercepts: utils.fixFloat(totalPercepts, 2),
      emitterCurp: company.curp || null,
      emitterEmployerRegistration: company.employerRegistration || null,
      emitterOriginEmployerRfc: employee.originEmployerCurp || null,
      receiverCurp: employee.curp,
      employeeId: employee.id,
      perceptsTotalEarnings: utils.fixFloat(totalPercepts, 2),
      perceptsTotalTaxed: utils.fixFloat(totalPercepts, 2),
      percepts,
    };

    if (!payroll.emitterCurp) delete payroll.emitterCurp;

    if (payroll.percepts.length === 0) delete payroll.percepts;

    const cfdi = {
      email: employee.email,
      name: employee.name,
      voucherExpeditionPlace: company.zipCode,
      voucherType: 'N',
      voucherCurrency: 'MXN',
      voucherSeries: 'N',
      voucherPaymentType: '99',
      voucherPaymentMethod: 'PUE',
      voucherDate: moment().unix(),
      voucherTotal: utils.fixFloat((totalPayroll), 2),
      emitterRegime: company.regime,
      emitterName: company.name,
      emitterRfc: company.rfc,
      receiverCfdiUsage: 'P01',
      receiverName: employee.name,
      concepts: [{
        productServiceKey: '84111505',
        description: 'Pago de nómina',
        quantity: 1,
        unitKey: 'ACT',
        amount: utils.fixFloat(totalPayroll, 2),
        unit: utils.fixFloat(totalPayroll, 2),
      }],
      complement: {
        payroll: Object.keys(payroll).reduce((accum, key) => {
          if (payroll[key]) set(accum, key, payroll[key]);
          return accum;
        }, {}),
      },
    };

    const payload = await api.invoice.saveSalaryReceipt(auth.companyId, cfdi);

    commit('setValues', {
      type: 'O',
      paymentDate: '',
      paymentPeriodStart: '',
      paymentPeriodEnd: '',
      earnedDays: '',
      percepts: [{
        perceptType: null,
        key: null,
        concept: null,
        taxedAmount: null,
        exemptAmount: null,
        overtime: [],
      }],
      deductions: [],
      otherPayments: [],
      paidLeaves: [],
    });


    commit('employee/setValues', {
      id: null,
      name: '',
      email: '',
      rfc: '',
      curp: '',
      socialSecurityNumber: '',
      jobRelationshipStart: '',
      seniority: '',
      contractType: '',
      unionized: '',
      workingDayType: '',
      regimeType: '',
      employedNumber: '',
      department: '',
      position: '',
      hazardPosition: '',
      paymentPeriodicity: '',
      bank: '',
      bankAccount: '',
      baseSalary: '',
      dailyBaseSalary: '',
      zipCode: '',
      localityKey: '',
    }, { root: true });

    return payload;
  },
  async getByUuid({ commit }, uuid) {
    const payload = await api.invoice.getByUuid(uuid);
    const {
      cfdi,
      operation,
      stamped,
    } = payload;

    cfdi.operation = operation;
    cfdi.operation.stamped = stamped;
    const { payroll } = cfdi.complement;
    commit('setValues', {
      type: payroll.payrollType,
      paymentDate: payroll.payrollPaymentDate,
      paymentPeriodStart: payroll.payrollPaymentPeriodStart,
      paymentPeriodEnd: payroll.payrollPaymentPeriodEnd,
      earnedDays: payroll.payrollEarnedDays,
      percepts: payroll.percepts,
      deductions: payroll.deductions ? payroll.deductions : [],
      otherPayments: payroll.otherPayments ? payroll.otherPayments : [],
      paidLeaves: payroll.paidLeaves ? payroll.paidLeaves : [],
    });
    commit(
      'employee/setValues',
      {
        email: operation.email,
        name: operation.name,
        rfc: operation.isSalaryReceipt ? null : cfdi.receiverRfc,
      },
      { root: true },
    );

    return payload;
  },
};
